import { Component, EventEmitter, Input, Output, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CourseStatus } from '@core/enums/course-status';
import { IModulesCourseDefinition } from '../../data/course-modules.data';
import { ModuleProgressService } from '../../services/module-progress.service';
import { CertificateService, IUserCourseCertificate } from '../../services/certificate.service';
import { forkJoin } from 'rxjs';

export interface IModuleCourseCard {
  course: IModulesCourseDefinition;
  status: CourseStatus;
}

@Component({
  selector: 'prism-module-card-list',
  templateUrl: './module-card-list.component.html',
  styleUrl: './module-card-list.component.scss'
})
export class ModuleCardListComponent implements OnInit {
  @Input() courses: IModulesCourseDefinition[] = [];
  @Output() courseClick = new EventEmitter<IModulesCourseDefinition>();

  private progress = inject(ModuleProgressService);
  private certificateService = inject(CertificateService);
  private cdr = inject(ChangeDetectorRef);

  cards: IModuleCourseCard[] = [];
  private certificates: IUserCourseCertificate[] = [];
  CourseStatus = CourseStatus;

  ngOnInit(): void {
    this.certificateService.getMyCertificates().subscribe(certs => {
      this.certificates = certs;

      // Load progress for all courses
      const loads = this.courses.map(c => this.progress.loadCourseProgress(c.id, true));

      if (loads.length === 0) {
        this.buildCards();
        return;
      }

      forkJoin(loads).subscribe(() => {
        this.buildCards();
      });
    });
  }

  private buildCards(): void {
    this.cards = this.courses.map(course => {
      const moduleIds = course.modules.map(m => m.id);
      const hasCert = this.certificates.some(c => c.courseId === course.id);

      let status: CourseStatus;
      if (hasCert) {
        status = CourseStatus.Completed;
      } else if (this.progress.isCourseComplete(course.id, moduleIds)) {
        // All modules done but no certificate (earned one for another course this year)
        status = CourseStatus.Reviewed;
      } else {
        status = this.progress.getCourseStatus(course.id, moduleIds);
      }

      return { course, status };
    });
    this.cdr.markForCheck();
  }

  view(card: IModuleCourseCard): void {
    this.courseClick.emit(card.course);
  }

  getCardAriaLabel(card: IModuleCourseCard): string {
    const parts: string[] = [];

    const status = card?.status;
    if (
      status === CourseStatus.Completed ||
      status === CourseStatus.Reviewed ||
      status === CourseStatus.Incomplete
    ) {
      parts.push(`Course status: ${status}`);
    }

    if (card?.course?.name) parts.push(card.course.name);
    if (card?.course?.description) parts.push(card.course.description);
    if (card?.course?.imageAlt) parts.push(card.course.imageAlt);

    return parts.join('. ');
  }

}
